import { Add } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Box, IconButton, Typography } from "@mui/material";
import { useContext } from "react";
import { connect } from "react-redux";
import { selectModeData, selectProject } from "../../../redux/selectors/editor";
import { FirebaseContext } from "../../firebase/FirebaseProvider";
import { Sheet, Sheets } from "../GridView";
import ConditionalFormattingItem, {ConditionalFormattingDefinition} from "./ConditionalFormattingItem";
import { getFormatRuleName, RuleDefinition } from "./FormatRule";
import { Constants } from "../../graph/StateTags";
import {isTagEntry, RowDef, TagCellType} from "../cells/cellUtils";
import { getTagDisplayValue } from "../../../utility/constantsTagsUtils";
import makeStyles from "@mui/styles/makeStyles";
import {ColumnDetails, CustomGridColDef, getColumnDetails} from "../gridColumnDefs";
import { selectConstants, selectSheets } from "../../../redux/selectors/firebase";
import {ISheetModeData} from "../../../redux/slices/editor";

const useStyles = makeStyles((theme) => ({
    container: {
        height: "100%",
        overflow: "auto"
    }
}));

interface Props
{
    sheets: Sheets
    project: string
    constants: Constants
    modeData: ISheetModeData
}
function ConditionalFormatting({ sheets, project, modeData, constants }: Props)
{
    const classes = useStyles();
    const { push } = useContext(FirebaseContext);

    const id = modeData.sheetId

    if (!id || !(id in sheets))
        return null

    const sheet = sheets[id];

    const handleClick = () =>
    {
        push(`sheets/${project}/${id}/conditionalFormatting/`, {
            formatStyle: {
                bold: false,
                italic: false,
                underline: false,
                strike: false,
                textColor: null,
                backColor: null
            },
            rule: {
                columnId: "",
                operator: "isEmpty",
                value: ""
            }
        });
    }
    const getSummary = (columnDetails: ColumnDetails, format: ConditionalFormattingDefinition) => {
        const value = columnDetails?.valueOptions ? columnDetails.valueOptions[format.rule.value] :
            columnDetails.type === "date" ? new Date(format.rule.value).toLocaleString():
                String(format.rule.value ?? "");

        return `${columnDetails?.name} ${getFormatRuleName(format.rule.operator)} ${value}`;
    };

    return <Box className={classes.container}>
        {Object.entries(sheet.conditionalFormatting ?? {}).map(([ruleId, format]) =>
        {
            const columnDetails = getColumnDetails([format.rule.columnId, sheet.columns[format.rule.columnId]], constants);
            return <Box key={ruleId}>
                <Accordion TransitionProps={{ timeout: 150 }} variant="outlined" >
                    <AccordionSummary sx={{ margin: 0 }}>
                        <Typography>{columnDetails && getSummary(columnDetails, format)}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <ConditionalFormattingItem sheetId={id} sheet={sheet} ruleId={ruleId} />
                    </AccordionDetails>
                </Accordion>
            </Box>
        })}
        <IconButton onClick={handleClick}>
            <Add />
        </IconButton>
    </Box>
}

const mapStateToProps = (store: never) => ({
    project: selectProject(store),
    constants: selectConstants(store) ?? {},
    modeData: selectModeData(store) ?? {} as ISheetModeData,
    sheets: selectSheets(store) ?? {}
});
export default connect(mapStateToProps, {
})(ConditionalFormatting);

function numberCheck(value: unknown, condition: (value: number) => boolean): boolean
{
    if (isTagEntry(value))
    {
        const values = value.tags.map((tag) => Number(tag.value));
        return values.some(condition);
    }
    else if (typeof value === "number")
    {
        return condition(value);
    }

    return false;
}

function booleanCheck(value: unknown, condition: (value: boolean) => boolean): boolean
{
    if (isTagEntry(value))
    {
        const values = value.tags.map((tag) => tag.value as boolean);
        return values.some(condition);
    }
    else if (typeof value === "boolean")
    {
        return condition(value);
    }

    return false;
}

function stringCheck(value: unknown, condition: (value: string) => boolean, constants: Constants): boolean
{
    if (isTagEntry(value))
    {
        const displayValues = value.tags.map((tag) => String(getTagDisplayValue(tag, constants)));

        return displayValues.some(condition);
    }

    return condition(String(value));
}

function dateCheck(value: unknown, condition: (value: Date, ruleValue: Date) => boolean, ruleValue?: number): boolean
{
    if (ruleValue)
    { 
        const ruleValueDate = new Date(ruleValue);
        const dateValue = value as Date | undefined;

        if (dateValue?.getTime)
        {
            return condition(dateValue, ruleValueDate);
        } else {
            const tagsValue = value as {tags: {type: string, value: number}[]};
            
            return tagsValue?.tags.some(tag => condition(new Date(tag.value), ruleValueDate));
        }
    }
    return false;
}

function singleSelectCheck(value: unknown, condition: (value: number) => boolean): boolean
{
    return typeof value === "number" ? condition(value) : false;
}

function checkRule(rule: RuleDefinition, column: CustomGridColDef, row: RowDef, constants: Constants): boolean
{
    const getTagNumberValue = (row: RowDef, column: string) => {
        const cell = row[column as string] as TagCellType;

        if (cell?.tags) {
            const tag = cell.tags.find(tag => tag.type === rule.value);

            if (Number.isInteger(tag?.value)) {
                return tag?.value as number;
            }
        }
        return 0;
    }
    const value = row[column.field];

    if (rule.columnId === column.id)
    {
        switch (rule.operator)
        {
            case "empty":
                return !value;
            case "notEmpty":
                return !!value;
            case ">":
                return numberCheck(value, (value) => value > Number(rule.value));
            case ">=":
                return numberCheck(value, (value) => value >= Number(rule.value));
            case "<":
                return numberCheck(value, (value) => value < Number(rule.value));
            case "<=":
                return numberCheck(value, (value) => value <= Number(rule.value));
            case "==":
                return numberCheck(value, (value) => value === Number(rule.value));
            case "!=":
                return numberCheck(value, (value) => value !== Number(rule.value));
            case "stringContains":
                return stringCheck(value, (value) => value.includes(rule.value), constants);
            case "stringNotContains":
                return stringCheck(value, (value) => !value.includes(rule.value), constants);
            case "stringStartsWith":
                return stringCheck(value, (value) => value.startsWith(rule.value), constants);
            case "stringEndsWith":
                return stringCheck(value, (value) => value.endsWith(rule.value), constants);
            case "stringEquals":
                return stringCheck(value, (value) => value === rule.value, constants);
            case "booleanIs":
                return booleanCheck(value, (value) => value === rule.value);
            case "dateIs":
                return dateCheck(value, (value, ruleValue) => value.getTime() === ruleValue.getTime(), rule.value);
            case "dateIsBefore":
                return dateCheck(value, (value, ruleValue) => value.getTime() < ruleValue.getTime(), rule.value);
            case "dateIsAfter":
                return dateCheck(value, (value, ruleValue) => value.getTime() > ruleValue.getTime(), rule.value);
            case "dateIsBeforeColumn":
                return dateCheck(value, (value, ruleValue) => value.getTime() < ruleValue.getTime(),
                    getTagNumberValue(row, rule.value));
            case "dateIsAfterColumn":
                return dateCheck(value, (value, ruleValue) => value.getTime() > ruleValue.getTime(),
                    getTagNumberValue(row, rule.value));
            case "singleSelectIs":
                return singleSelectCheck(value, (value) => value === rule.value);
        }

    }
    return false;
}

export function getActiveFormatClassNames(column: CustomGridColDef, row: RowDef, sheet: Sheet, constants: Constants): string[]
{
    const ids: string[] = [];
    Object.entries(sheet.conditionalFormatting ?? {}).forEach(([id, formatRule]) =>
    {
        if (checkRule(formatRule.rule, column, row, constants))
        {
            ids.push(`style${id}`);
        }
    });

    return ids;
}